import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link2, Mail, MessageCircle, Ban, Eye, Clock, CheckCircle2, CircleDashed, Copy, Loader2, Bell, ChevronDown, ChevronRight, Plus, Download, X, Play } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DocPreviewModal from "@/pages/DocPreviewModal";

const PRESETS = ["Passport Copy", "Proof of Address", "Bank Statement", "Utility Bill", "Video Recording (15 sec)"];

const STATUS_BADGE = {
  pending: { t: "Pending", c: "border-amber-200 bg-amber-50 text-amber-700", Icon: Clock },
  partial: { t: "Partially uploaded", c: "border-blue-200 bg-blue-50 text-blue-700", Icon: CircleDashed },
  completed: { t: "Completed", c: "border-green-200 bg-green-50 text-green-700", Icon: CheckCircle2 },
};

const fmt = (t) => { try { return new Date(t).toLocaleString(); } catch { return t; } };

const buildActivity = (l) => {
  const ev = [];
  if (l.created_at) ev.push({ t: l.created_at, label: "Link created", Icon: Link2 });
  if (l.emailed_at) ev.push({ t: l.emailed_at, label: `Emailed to ${l.client_email}`, Icon: Mail });
  if (l.reminder_sent_at) ev.push({ t: l.reminder_sent_at, label: "Expiry reminder sent", Icon: Clock });
  if (l.first_opened_at) ev.push({ t: l.first_opened_at, label: "Client opened the link", Icon: Eye });
  (l.documents || []).forEach((d) => { if (d.uploaded && d.uploaded_at) ev.push({ t: d.uploaded_at, label: `Uploaded: ${d.name}`, Icon: CheckCircle2 }); });
  if (l.last_opened_at && l.open_count > 1) ev.push({ t: l.last_opened_at, label: `Opened again (×${l.open_count})`, Icon: Eye });
  if (l.revoked_at) ev.push({ t: l.revoked_at, label: "Link revoked", Icon: Ban });
  return ev.sort((a, b) => new Date(a.t) - new Date(b.t));
};

const isNudgeable = (l) => !l.revoked && !l.expired && (!l.opened || l.status !== "completed");

export default function AdminLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);
  const [nudgingAll, setNudgingAll] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [gEmail, setGEmail] = useState(""); const [gName, setGName] = useState("");
  const [gPhone, setGPhone] = useState(""); const [gMsg, setGMsg] = useState("");
  const [gDocs, setGDocs] = useState(["Passport Copy", "Proof of Address"]);
  const [gInput, setGInput] = useState(""); const [gBusy, setGBusy] = useState(false);
  const [gVideoInstr, setGVideoInstr] = useState("");
  const addGDoc = (n) => { n = (n || "").trim(); if (!n || gDocs.some((d) => d.toLowerCase() === n.toLowerCase())) return; setGDocs((d) => [...d, n]); setGInput(""); };
  const genLink = async () => {
    if (gDocs.length === 0) return toast.error("Add at least one document.");
    if (!gEmail.includes("@")) return toast.error("Enter a valid client email.");
    setGBusy(true);
    try {
      await api.post("/admin/upload-links", { client_email: gEmail, client_name: gName, client_phone: gPhone, message: gMsg, documents: gDocs, video_instructions: gVideoInstr });
      toast.success("Link generated — share it via Email or WhatsApp below.");
      setGEmail(""); setGName(""); setGPhone(""); setGMsg(""); setGVideoInstr(""); setShowGen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate link."); } finally { setGBusy(false); }
  };

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/upload-links"); setLinks(r.data.links || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => links.filter((l) => {
    if (filter === "not-opened") return !l.opened && !l.revoked && !l.expired;
    if (filter === "incomplete") return l.status !== "completed" && !l.revoked && !l.expired;
    return true;
  }), [links, filter]);

  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Link copied."); };
  const nudge = async (token) => {
    setBusy(token);
    try { await api.post(`/admin/upload-links/${token}/send-email`); toast.success("Nudge email sent."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send nudge."); } finally { setBusy(null); }
  };
  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The client will no longer be able to use it.")) return;
    setBusy(token);
    try { await api.post(`/admin/upload-links/${token}/revoke`); toast.success("Link revoked."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke."); } finally { setBusy(null); }
  };
  const whatsapp = (l) => {
    const digits = (l.client_phone || "").replace(/[^0-9]/g, "");
    const text = encodeURIComponent(`Hi ${l.client_name || "there"}, please upload your documents for UniIncorp using this secure link: ${l.public_url}`);
    window.open(`https://wa.me/${digits}?text=${text}`, "_blank", "noopener");
  };
  const nudgeAll = async () => {
    const targets = shown.filter(isNudgeable);
    if (targets.length === 0) return toast.info("No unopened or incomplete links to nudge.");
    if (!window.confirm(`Send a nudge email to ${targets.length} client(s)?`)) return;
    setNudgingAll(true);
    let ok = 0;
    for (const l of targets) {
      try { await api.post(`/admin/upload-links/${l.token}/send-email`); ok += 1; } catch { /* skip */ }
    }
    toast.success(`Nudged ${ok}/${targets.length} client(s).`);
    setNudgingAll(false);
    await load();
  };

  const counts = useMemo(() => ({
    all: links.length,
    notOpened: links.filter((l) => !l.opened && !l.revoked && !l.expired).length,
    incomplete: links.filter((l) => l.status !== "completed" && !l.revoked && !l.expired).length,
  }), [links]);

  return (
    <div className="mt-8" data-testid="admin-links">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-sm border border-border bg-card p-1">
          {[["all", `All (${counts.all})`], ["not-opened", `Not opened (${counts.notOpened})`], ["incomplete", `Incomplete (${counts.incomplete})`]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-sm text-xs transition-colors ${filter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`links-filter-${k}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" disabled={nudgingAll} onClick={nudgeAll} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-8 text-xs" data-testid="links-nudge-all">
          {nudgingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Bell className="h-3.5 w-3.5 mr-1" /> Nudge unopened / incomplete</>}
        </Button>
        <Button size="sm" onClick={() => setShowGen((s) => !s)} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid="links-generate-toggle">
          <Plus className="h-3.5 w-3.5 mr-1" /> Generate new link
        </Button>
      </div>

      {showGen && (
        <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50/40 p-4 space-y-3" data-testid="links-generate-panel">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="Client email" className="h-8 bg-card border-border text-sm" data-testid="links-gen-email" />
            <Input value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Client name (optional)" className="h-8 bg-card border-border text-sm" data-testid="links-gen-name" />
            <Input value={gPhone} onChange={(e) => setGPhone(e.target.value)} placeholder="WhatsApp number (optional)" className="h-8 bg-card border-border text-sm" data-testid="links-gen-phone" />
          </div>
          <Input value={gMsg} onChange={(e) => setGMsg(e.target.value)} placeholder="Message to the client (optional)" className="h-8 bg-card border-border text-sm" data-testid="links-gen-message" />
          <div className="flex flex-wrap gap-1.5">
            {gDocs.map((d, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-orange-200 bg-orange-100 px-2 py-1 text-xs text-orange-800" data-testid={`links-gen-chip-${i}`}>
                {d}<button onClick={() => setGDocs((x) => x.filter((_, idx) => idx !== i))} className="hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Input value={gInput} onChange={(e) => setGInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGDoc(gInput))} placeholder="Add a document name…" className="h-8 bg-card border-border text-sm" data-testid="links-gen-doc-input" />
            <Button size="sm" variant="outline" onClick={() => addGDoc(gInput)} className="h-8 rounded-sm border-slate-300 bg-card text-xs shrink-0" data-testid="links-gen-doc-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.filter((p) => !gDocs.some((d) => d.toLowerCase() === p.toLowerCase())).map((p) => (
              <button key={p} onClick={() => addGDoc(p)} className="rounded-sm border border-dashed border-slate-300 bg-card px-2 py-0.5 text-[11px] text-muted-foreground hover:border-orange-300 hover:text-orange-700" data-testid={`links-gen-preset-${p.replace(/\s+/g, "-")}`}>+ {p}</button>
            ))}
          </div>
          {gDocs.some((d) => d.toLowerCase() === "video recording (15 sec)") && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Video Instructions</label>
              <Input value={gVideoInstr} onChange={(e) => setGVideoInstr(e.target.value)} placeholder="E.g. Please record a short video showing your face clearly and reading out your full name and date of birth." className="mt-1 h-8 bg-card border-border text-sm" data-testid="links-gen-video-instructions" />
            </div>
          )}
          <Button size="sm" disabled={gBusy} onClick={genLink} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid="links-gen-submit">
            {gBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Link2 className="h-3.5 w-3.5 mr-1" /> Generate secure link</>}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>
      ) : shown.length === 0 ? (
        <p className="text-muted-foreground py-16 text-center" data-testid="links-empty">No upload links found.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {shown.map((l) => {
            const b = STATUS_BADGE[l.status] || STATUS_BADGE.pending;
            const acts = buildActivity(l);
            const isOpen = open === l.token;
            return (
              <div key={l.token} className="rounded-xl border border-border bg-card p-4 card-soft" data-testid={`links-row-${l.token}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${b.c}`} data-testid={`links-status-${l.token}`}><b.Icon className="h-3 w-3" /> {b.t}</span>
                    {l.opened
                      ? <span className="inline-flex items-center gap-1 rounded-sm border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-green-700" data-testid={`links-opened-${l.token}`}><Eye className="h-3 w-3" /> Opened{l.open_count > 1 ? ` ×${l.open_count}` : ""}</span>
                      : <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`links-not-opened-${l.token}`}>Not opened</span>}
                    <span className="text-xs text-muted-foreground">{l.uploaded_count}/{l.doc_count} uploaded</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">{l.req_id ? "Request" : "Standalone"}</span>
                    {l.revoked && <span className="text-[10px] uppercase tracking-wider text-red-600 border border-red-200 bg-red-50 rounded-sm px-1.5 py-0.5">Revoked</span>}
                    {!l.revoked && l.expired && <span className="text-[10px] uppercase tracking-wider text-red-600 border border-red-200 bg-red-50 rounded-sm px-1.5 py-0.5">Expired</span>}
                  </div>
                  <span className="text-xs text-muted-foreground truncate">{l.client_name ? `${l.client_name} · ` : ""}{l.client_email}</span>
                </div>

                <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`links-dates-${l.token}`}>Generated {fmt(l.created_at)} · Expires {fmt(l.expires_at)}</div>

                <div className="mt-2 flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1">
                  <span className="text-[11px] font-mono text-muted-foreground truncate flex-1">{l.public_url}</span>
                  <button onClick={() => copy(l.public_url)} className="text-muted-foreground hover:text-orange-700 shrink-0" data-testid={`links-copy-${l.token}`}><Copy className="h-3.5 w-3.5" /></button>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {isNudgeable(l) && (
                    <Button size="sm" variant="outline" disabled={busy === l.token} onClick={() => nudge(l.token)} className="h-7 rounded-sm border-orange-300 text-orange-700 bg-card hover:bg-orange-50 text-xs" data-testid={`links-nudge-${l.token}`}>
                      {busy === l.token ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Bell className="h-3.5 w-3.5 mr-1" /> Nudge</>}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => whatsapp(l)} className="h-7 rounded-sm border-green-300 text-green-700 bg-card hover:bg-green-50 text-xs" data-testid={`links-whatsapp-${l.token}`}><MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button>
                  {!l.revoked && !l.expired && (
                    <Button size="sm" variant="outline" disabled={busy === l.token} onClick={() => revoke(l.token)} className="h-7 rounded-sm border-red-200 text-red-600 bg-card hover:bg-red-50 text-xs" data-testid={`links-revoke-${l.token}`}><Ban className="h-3.5 w-3.5 mr-1" /> Revoke</Button>
                  )}
                  <button onClick={() => setOpen(isOpen ? null : l.token)} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`links-activity-toggle-${l.token}`}>
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Activity ({acts.length})
                  </button>
                </div>

                {isOpen && (
                  <div className="mt-3 border-t border-border/60 pt-3 space-y-3" data-testid={`links-activity-${l.token}`}>
                    <div className="space-y-1.5">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Documents</p>
                      {(l.documents || []).map((d) => (
                        <div key={d.id} className="flex items-center gap-2 text-xs rounded-sm bg-secondary/40 px-2 py-1.5" data-testid={`links-doc-${d.id}`}>
                          <span className={`grid place-items-center h-6 w-6 rounded-sm shrink-0 ${d.uploaded ? "bg-green-100 text-green-600" : "bg-secondary text-muted-foreground"}`}>{d.uploaded ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground truncate">{d.name}</p>
                            <p className="text-muted-foreground truncate text-[11px]">{d.uploaded ? `${d.filename || "Uploaded"} · ${fmt(d.uploaded_at)}` : "Awaiting upload"}</p>
                          </div>
                          {d.uploaded && d.path && d.kind === "video" && (
                            <Button size="sm" variant="outline" onClick={() => setPreview(d)} className="h-7 rounded-sm border-slate-300 bg-card text-xs shrink-0" data-testid={`links-play-${d.id}`}><Play className="h-3.5 w-3.5 mr-1" /> Play</Button>
                          )}
                          {d.uploaded && d.path && (
                            <a href={`${API}/admin/files/${d.path}`} target="_blank" rel="noreferrer" download className="shrink-0">
                              <Button size="sm" variant="outline" className="h-7 rounded-sm border-slate-300 bg-card text-xs" data-testid={`links-download-${d.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Activity</p>
                      {acts.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="grid place-items-center h-6 w-6 rounded-sm bg-secondary text-muted-foreground shrink-0"><e.Icon className="h-3.5 w-3.5" /></span>
                          <span className="text-foreground">{e.label}</span>
                          <span className="ml-auto text-muted-foreground font-mono text-[11px]">{fmt(e.t)}</span>
                        </div>
                      ))}
                    </div>
                    <DocPreviewModal doc={preview} onClose={() => setPreview(null)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
